import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface AuthState {
  user: User | null;
  loading: boolean;
  emailVerified: boolean;
  setUser: (user: User | null) => void;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateEmail: (email: string) => Promise<void>;
  updateProfile: (data: { full_name?: string; avatar_url?: string }) => Promise<void>;
  resendVerification: () => Promise<void>;
  initialize: () => () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        loading: true,
        emailVerified: false,

        setUser: (user) =>
          set({
            user,
            emailVerified: user?.email_confirmed_at != null,
          }),

        signOut: async () => {
          try {
            await supabase.auth.signOut();
            set({ user: null, emailVerified: false });
            toast.success('Signed out successfully');
          } catch (error) {
            console.error('Sign out error:', error);
            toast.error('Failed to sign out');
          }
        },

        resetPassword: async (email: string) => {
          try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
              redirectTo: `${window.location.origin}/auth/reset-password`,
            });
            if (error) throw error;
            toast.success('Password reset instructions sent to your email');
          } catch (error) {
            console.error('Password reset error:', error);
            toast.error('Failed to send password reset email');
            throw error;
          }
        },

        updateEmail: async (email: string) => {
          try {
            const { error } = await supabase.auth.updateUser({ email });
            if (error) throw error;
            toast.success('Email update confirmation sent');
          } catch (error) {
            console.error('Email update error:', error);
            toast.error('Failed to update email');
            throw error;
          }
        },

        updateProfile: async (data) => {
          const { user } = get();
          try {
            const { error } = await supabase.auth.updateUser({
              data: {
                ...user?.user_metadata,
                ...data,
              },
            });
            if (error) throw error;
            toast.success('Profile updated successfully');
          } catch (error) {
            console.error('Profile update error:', error);
            toast.error('Failed to update profile');
            throw error;
          }
        },

        resendVerification: async () => {
          const { user } = get();
          if (!user?.email) return;

          try {
            const { error } = await supabase.auth.resend({
              type: 'signup',
              email: user.email,
            });
            if (error) throw error;
            toast.success('Verification email resent! Please check your inbox.');
          } catch (error) {
            console.error('Failed to resend verification:', error);
            toast.error('Failed to resend verification email');
          }
        },

        initialize: () => {
          // Get initial session
          supabase.auth.getSession().then(({ data: { session } }) => {
            set({
              user: session?.user ?? null,
              emailVerified: session?.user?.email_confirmed_at != null,
              loading: false,
            });
          });

          // Listen for auth changes
          const {
            data: { subscription },
          } = supabase.auth.onAuthStateChange((_event, session) => {
            set({
              user: session?.user ?? null,
              emailVerified: session?.user?.email_confirmed_at != null,
            });
          });

          return () => subscription.unsubscribe();
        },
      }),
      {
        name: 'jmefit-auth',
        partialize: (state) => ({
          user: state.user,
          emailVerified: state.emailVerified,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);
